import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import MiraWidget from './MiraWidget';

/**
 * MiraGlobalMount — mount ONE Mira instance in the App shell and let it adapt
 * to the current route. Suppressed on admin, walking mode, and error pages.
 *
 * Route-based behavior (only these matter now; everything else = generic):
 *   /c/:code                       → dwell trigger + card_code
 *   /canvas/idea/:submissionId     → always trigger + submission_id
 *   /admin*                        → hidden
 *   /series/:seriesId/walk/:code   → hidden (walking mode, don't distract)
 *   * (everything else)            → always trigger, generic
 */

// Routes where Mira must stay out of the way. Admin needs the console
// noise-free; the walking mode is deliberately meditative. Everywhere
// else — including /food (diners in ideation mode love Mira) — Mira is
// available on the bottom-right floating chip.
const HIDE_MATCHES = ['/admin', '/admin/*', '/series/:seriesId/walk/:seriesCode', '/mira', '/table', '/wall'];

export default function MiraGlobalMount() {
  const loc = useLocation();

  const hidden = HIDE_MATCHES.some((p) => matchPath({ path: p, end: p === '/admin' ? true : false }, loc.pathname));
  if (hidden) return null;

  // /c/:code — capture page (dwell + card context). Deck guard is server-side.
  const captureMatch = matchPath({ path: '/c/:code', end: true }, loc.pathname);
  if (captureMatch) {
    return <MiraWidget card_code={captureMatch.params.code} trigger="dwell" pageKey={`capture:${captureMatch.params.code}`} />;
  }

  // /canvas/idea/:submissionId — always visible with submission context
  const ideaMatch = matchPath({ path: '/canvas/idea/:submissionId', end: true }, loc.pathname);
  if (ideaMatch) {
    return <MiraWidget submission_id={ideaMatch.params.submissionId} trigger="always" pageKey={`idea:${ideaMatch.params.submissionId}`} />;
  }

  // Everything else — landing, /canvas, /challenges, /showcase, /made-real, /who/*, /about, /story, /food, /wall …
  return <MiraWidget trigger="always" pageKey={`page:${loc.pathname}`} />;
}
